import {
  type ADFDocumentContentNode,
  type ADFDocument,
  type ADFMark,
} from '@canvas/adf-types';

import { validateAdf } from './adf-validation.ts';
import { plainTextToAdf } from './conversions.ts';

type FromStringToADFOptions = {
  trimWhitespaces?: boolean;
};

export const fromString = (
  input?: string,
  marks?: ADFMark[],
  { trimWhitespaces }: FromStringToADFOptions = {
    trimWhitespaces: true,
  },
): ADFDocument => {
  // Normalise line endings
  const inputNormalized = input?.replace(/\r\n/, '\n');

  // Split into lines and remove trailing whitespace
  let lines: string[] = [];
  if (trimWhitespaces) {
    lines = inputNormalized?.split('\n')?.map((line) => line.replace(/\s+$/, '')) ?? [''];
  } else {
    lines = inputNormalized?.split('\n') ?? [''];
  }

  return {
    version: 1,
    type: 'doc',
    content: lines.map((line) => ({
      type: 'paragraph',
      content:
        line.length > 0
          ? [
              {
                type: 'text',
                text: line,
                marks: marks?.length === 0 ? undefined : marks,
              },
            ]
          : [],
    })) as [ADFDocumentContentNode, ...ADFDocumentContentNode[]],
  };
};

export const adfFromString = (input: string): ADFDocument => {
  try {
    const parsed = JSON.parse(input);

    // JSON.parse accepts inputs like `123`, `[]`, `null`, `true`, and so we
    // need to validate that the parsed result is a valid ADF object, otherwise fall back
    // to plain text.
    if (!validateAdf(parsed)) {
      throw new Error(
        'Parsed input is not a valid ADF object, falling back to plain text',
      );
    }

    return parsed;
  } catch (_err) {
    return plainTextToAdf(input);
  }
};
